import React, {useEffect} from 'react';
import {Box, Button, TextField, Popper, ButtonGroup, Chip, Stack} from '@mui/material';
import PropTypes from 'prop-types';
import {ezprOrange} from '../../_helpers';
import {formatMaterialName, removeLeadingZeros} from '../../../utils/helpers';
import {PurchaseRequest} from '../../../models/PurchaseRequest';
import {FilterAutocomplete} from './FilterAutocomplete.jsx';
import {BrickContext} from 'olympe';

/**
 * @param {FilterView.propTypes} props
 * @return {Element}
 * @constructor
 */
export function FilterView(props) {
    /**
     * Every time a filter is selected
     * - set Filters
     */
    useEffect(() => {
        props.setFilters(props.selectedFilters);
    }, [props.selectedFilters]);

    const COUNT_OF_LIMIT_TAGS = 2;

    /**
     * Calculate date range based on preset
     * @param {string} preset - The date preset ('lastYear', 'thisYear', 'last6Months', 'lastMonth')
     * @return {Object} - Object with startDate and endDate
     */
    const getDateRangeFromPreset = (preset) => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();

        switch (preset) {
        case 'lastYear':
            return {
                startDate: `${currentYear - 1}-01-01`,
                endDate: `${currentYear - 1}-12-31`,
            };
        case 'thisYear':
            return {
                startDate: `${currentYear}-01-01`,
                endDate: today.toISOString().split('T')[0],
            };
        case 'last6Months': {
            const sixMonthsAgo = new Date(today);
            sixMonthsAgo.setMonth(currentMonth - 6);
            return {
                startDate: sixMonthsAgo.toISOString().split('T')[0],
                endDate: today.toISOString().split('T')[0],
            };
        }
        case 'lastMonth': {
            const lastMonth = new Date(today);
            lastMonth.setMonth(currentMonth - 1, 1);
            const lastMonthEnd = new Date(today);
            lastMonthEnd.setDate(0); // Last day of previous month
            return {
                startDate: lastMonth.toISOString().split('T')[0],
                endDate: lastMonthEnd.toISOString().split('T')[0],
            };
        }
        default:
            return {startDate: '', endDate: ''};
        }
    };

    /**
     * Handle date preset selection for creation date
     * @param {string} preset - The selected preset
     */
    const handleCreationDatePreset = (preset) => {
        const {startDate, endDate} = getDateRangeFromPreset(preset);
        props.setSelectedFilters({
            ...props.selectedFilters,
            startCreationDate: {selected: startDate},
            endCreationDate: {selected: endDate},
            creationDatePreset: preset,
        });
    };

    /**
     * Handle date preset selection for delivery date
     * @param {string} preset - The selected preset
     */
    const handleDeliveryDatePreset = (preset) => {
        const {startDate, endDate} = getDateRangeFromPreset(preset);
        props.setSelectedFilters({
            ...props.selectedFilters,
            startRequestedDeliveryDate: {selected: startDate},
            endRequestedDeliveryDate: {selected: endDate},
            deliveryDatePreset: preset,
        });
    };

    /**
     * Handle manual date input change
     * @param {string} field - The field name (startCreationDate, endCreationDate, etc.)
     * @param {string} value - The date value
     */
    const handleDateInputChange = (field, value) => {
        props.setSelectedFilters({
            ...props.selectedFilters,
            [field]: {selected: value},
            // Clear preset when manual input is used
            ...(field.includes('Creation') ? {creationDatePreset: null} : {deliveryDatePreset: null}),
        });
    };

    const autocompletePopperComponent = function(params, key) {
        return (
            <Popper {...params} className='custom-dropdown-menu-with-buttons'>
                <ButtonGroup color="primary" aria-label="outlined primary button group">
                    <Button
                        variant='text'
                        onClick={() => {
                            props.setSelectedFilters({
                                ...props.selectedFilters,
                                [key]: {
                                    ...props.selectedFilters[key],
                                    isInclude: true,
                                },
                            });
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{color: props.selectedFilters[key].isInclude ? ezprOrange : '#CCC'}}
                    >
                        Include
                    </Button>
                    <Button
                        variant='text'
                        onClick={() => {
                            props.setSelectedFilters({
                                ...props.selectedFilters,
                                [key]: {
                                    ...props.selectedFilters[key],
                                    isInclude: false,
                                },
                            });
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{color: !props.selectedFilters[key].isInclude ? ezprOrange : '#CCC'}}
                    >
                        Exclude
                    </Button>
                </ButtonGroup>
                {params.children}
            </Popper>
        );
    };

    const handleChangeFilterValue = (selectedValues, key) => {
        props.setSelectedFilters({
            ...props.selectedFilters,
            [key]: {
                ...props.selectedFilters[key],
                selected: selectedValues,
            },
        });
    };

    const handleClearOptions = (key) => {
        props.setSelectedFilters({
            ...props.selectedFilters,
            [key]: {
                ...props.selectedFilters[key],
                selected: [],
            },
        });
    };

    return <Box
        className={'popup-scroller'}
        sx={{
            width: 400,
            height: 'auto',
            backgroundColor: '#EFEFEF',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignContent: 'center',
            paddingX: 2,
            paddingTop: 2,
            overflowY: 'scroll',
        }}>
        <>
            <FilterAutocomplete
                id='spenderFilter'
                selectedObject={props.selectedFilters.spenders}
                label="Spender"
                handleChange={(selectedOptions) => handleChangeFilterValue(selectedOptions, 'spenders')}
                onClearOptions={() => handleClearOptions('spenders')}
                popperComponent={(val) => autocompletePopperComponent(val, 'spenders')}
                limitTags={COUNT_OF_LIMIT_TAGS}
                context={props.context}
                field={'spender'}
                myRequestOnly={!props.selectedFilters.allSelected}
                userId={props.userId}
                getFiltersDropdownsOptions={props.getFiltersDropdownsOptions}
                currentTab={props.currentTab}
            />
            <FilterAutocomplete
                id='requesterFilter'
                selectedObject={props.selectedFilters.requesters}
                label="Requester"
                handleChange={(selectedOptions) => handleChangeFilterValue(selectedOptions, 'requesters')}
                onClearOptions={() => handleClearOptions('requesters')}
                popperComponent={(val) => autocompletePopperComponent(val, 'requesters')}
                limitTags={COUNT_OF_LIMIT_TAGS}
                context={props.context}
                field={'requester'}
                myRequestOnly={!props.selectedFilters.allSelected}
                userId={props.userId}
                getFiltersDropdownsOptions={props.getFiltersDropdownsOptions}
                currentTab={props.currentTab}
            />
            <FilterAutocomplete
                id='projectTitleFilter'
                selectedObject={props.selectedFilters.projectTitle}
                label="Project title"
                handleChange={(selectedOptions) => handleChangeFilterValue(selectedOptions, 'projectTitle')}
                onClearOptions={() => handleClearOptions('projectTitle')}
                popperComponent={(val) => autocompletePopperComponent(val, 'projectTitle')}
                limitTags={COUNT_OF_LIMIT_TAGS}
                context={props.context}
                field={'projectTitle'}
                myRequestOnly={!props.selectedFilters.allSelected}
                userId={props.userId}
                getFiltersDropdownsOptions={props.getFiltersDropdownsOptions}
                currentTab={props.currentTab}
            />
            <FilterAutocomplete
                id='supplierFilter'
                selectedObject={props.selectedFilters.suppliers}
                label="Supplier"
                handleChange={(selectedOptions) => handleChangeFilterValue(selectedOptions, 'suppliers')}
                onClearOptions={() => handleClearOptions('suppliers')}
                popperComponent={(val) => autocompletePopperComponent(val, 'suppliers')}
                limitTags={COUNT_OF_LIMIT_TAGS}
                context={props.context}
                field={'vendor'}
                myRequestOnly={!props.selectedFilters.allSelected}
                userId={props.userId}
                getFiltersDropdownsOptions={props.getFiltersDropdownsOptions}
                currentTab={props.currentTab}
            />
            <FilterAutocomplete
                id='categoryFilter'
                selectedObject={props.selectedFilters.categories}
                label="Category"
                handleChange={(selectedOptions) => handleChangeFilterValue(selectedOptions, 'categories')}
                onClearOptions={() => handleClearOptions('categories')}
                popperComponent={(val) => autocompletePopperComponent(val, 'categories')}
                limitTags={COUNT_OF_LIMIT_TAGS}
                context={props.context}
                field={'l1Category'}
                myRequestOnly={!props.selectedFilters.allSelected}
                userId={props.userId}
                getFiltersDropdownsOptions={props.getFiltersDropdownsOptions}
                currentTab={props.currentTab}
            />
            <FilterAutocomplete
                id='materialFilter'
                selectedObject={props.selectedFilters.materials}
                label="Material"
                handleChange={(selectedOptions) => handleChangeFilterValue(selectedOptions, 'materials')}
                onClearOptions={() => handleClearOptions('materials')}
                popperComponent={(val) => autocompletePopperComponent(val, 'materials')}
                getOptionLabel={(option) => formatMaterialName(option.label)}
                limitTags={COUNT_OF_LIMIT_TAGS}
                context={props.context}
                field={'materials.name'}
                myRequestOnly={!props.selectedFilters.allSelected}
                getFiltersDropdownsOptions={props.getFiltersDropdownsOptions}
                userId={props.userId}
                currentTab={props.currentTab}
            />
        </>
        <div className={'filters-drawer-range-title'}>Creation date</div>
        <div className={'filters-drawer-range'}>
            <TextField
                sx={{width: '100%'}}
                type={'date'}
                label={'Start date'}
                variant={'outlined'}
                size={'small'}
                value={props.selectedFilters?.startCreationDate?.selected || ''}
                onChange={(event) => handleDateInputChange('startCreationDate', event.target.value)}
                InputLabelProps={{
                    shrink: true,
                }}/>
            <TextField
                sx={{width: '100%'}}
                type={'date'}
                label={'End date'}
                variant={'outlined'}
                size={'small'}
                value={props.selectedFilters?.endCreationDate?.selected || ''}
                onChange={(event) => handleDateInputChange('endCreationDate', event.target.value)}
                InputLabelProps={{
                    shrink: true,
                }}/>
        </div>
        <div className={'filters-drawer-presets'}>
            {[
                {key: 'lastYear', label: 'Last Year'},
                {key: 'thisYear', label: 'This Year'},
                {key: 'last6Months', label: 'Last 6 months'},
                {key: 'lastMonth', label: 'Last month'},
            ].map((preset) => (
                <Chip
                    key={preset.key}
                    label={preset.label}
                    variant={props.selectedFilters?.creationDatePreset === preset.key ? 'filled' : 'outlined'}
                    color={props.selectedFilters?.creationDatePreset === preset.key ? 'primary' : 'default'}
                    onClick={() => handleCreationDatePreset(preset.key)}
                    size="small"
                />
            ))}
        </div>
        <>
            <FilterAutocomplete
                id='idFilter'
                selectedObject={props.selectedFilters.ids}
                label="Easy Request PR ID"
                handleChange={(selectedOptions) => handleChangeFilterValue(selectedOptions, 'ids')}
                onClearOptions={() => handleClearOptions('ids')}
                popperComponent={(val) => autocompletePopperComponent(val, 'ids')}
                limitTags={COUNT_OF_LIMIT_TAGS}
                context={props.context}
                field={'id'}
                myRequestOnly={!props.selectedFilters.allSelected}
                userId={props.userId}
                getFiltersDropdownsOptions={props.getFiltersDropdownsOptions}
                currentTab={props.currentTab}
            />
            {props.currentTab === PurchaseRequest.tab.submitted && (
                <FilterAutocomplete
                    id='prNumberFilter'
                    selectedObject={props.selectedFilters.prNumber}
                    label="SAP PR Number"
                    handleChange={(selectedOptions) => handleChangeFilterValue(selectedOptions, 'prNumber')}
                    onClearOptions={() => handleClearOptions('prNumber')}
                    popperComponent={(val) => autocompletePopperComponent(val, 'prNumber')}
                    limitTags={COUNT_OF_LIMIT_TAGS}
                    context={props.context}
                    field={'prNumber'}
                    myRequestOnly={!props.selectedFilters.allSelected}
                    userId={props.userId}
                    getFiltersDropdownsOptions={props.getFiltersDropdownsOptions}
                    currentTab={props.currentTab}
                />
            )}
            <FilterAutocomplete
                id='statusFilter'
                selectedObject={props.selectedFilters.statuses}
                label="Status"
                handleChange={(selectedOptions) => handleChangeFilterValue(selectedOptions, 'statuses')}
                onClearOptions={() => handleClearOptions('statuses')}
                popperComponent={(val) => autocompletePopperComponent(val, 'statuses')}
                limitTags={COUNT_OF_LIMIT_TAGS}
                context={props.context}
                field={'status'}
                myRequestOnly={!props.selectedFilters.allSelected}
                userId={props.userId}
                getFiltersDropdownsOptions={props.getFiltersDropdownsOptions}
                currentTab={props.currentTab}
            />
            <FilterAutocomplete
                id='fromFilter'
                selectedObject={props.selectedFilters.sources}
                label="From"
                handleChange={(selectedOptions) => handleChangeFilterValue(selectedOptions, 'sources')}
                onClearOptions={() => handleClearOptions('sources')}
                popperComponent={(val) => autocompletePopperComponent(val, 'sources')}
                limitTags={COUNT_OF_LIMIT_TAGS}
                context={props.context}
                field={'source'}
                myRequestOnly={!props.selectedFilters.allSelected}
                userId={props.userId}
                getFiltersDropdownsOptions={props.getFiltersDropdownsOptions}
                currentTab={props.currentTab}
            />
            <FilterAutocomplete
                id='wobFilter'
                selectedObject={props.selectedFilters.wayOfBuying}
                label="Way of Buying"
                handleChange={(selectedOptions) => handleChangeFilterValue(selectedOptions, 'wayOfBuying')}
                onClearOptions={() => handleClearOptions('wayOfBuying')}
                popperComponent={(val) => autocompletePopperComponent(val, 'wayOfBuying')}
                limitTags={COUNT_OF_LIMIT_TAGS}
                context={props.context}
                field={'wayOfBuying'}
                myRequestOnly={!props.selectedFilters.allSelected}
                userId={props.userId}
                getFiltersDropdownsOptions={props.getFiltersDropdownsOptions}
                currentTab={props.currentTab}
            />
            <FilterAutocomplete
                id='companyCodeFilter'
                selectedObject={props.selectedFilters.companyCode}
                label="Company Code"
                handleChange={(selectedOptions) => handleChangeFilterValue(selectedOptions, 'companyCode')}
                onClearOptions={() => handleClearOptions('companyCode')}
                popperComponent={(val) => autocompletePopperComponent(val, 'companyCode')}
                limitTags={COUNT_OF_LIMIT_TAGS}
                context={props.context}
                field={'companyCode'}
                myRequestOnly={!props.selectedFilters.allSelected}
                userId={props.userId}
                getFiltersDropdownsOptions={props.getFiltersDropdownsOptions}
                loading={props.isLoadingAuthData}
                filteredOptions={props.filteredOptions}
                currentTab={props.currentTab}
            />
            <FilterAutocomplete
                id='purchaseOrgFilter'
                selectedObject={props.selectedFilters.purchaseOrganization}
                label="Purchase Organization"
                handleChange={(selectedOptions) => handleChangeFilterValue(selectedOptions, 'purchaseOrganization')}
                onClearOptions={() => handleClearOptions('purchaseOrganization')}
                popperComponent={(val) => autocompletePopperComponent(val, 'purchaseOrganization')}
                limitTags={COUNT_OF_LIMIT_TAGS}
                context={props.context}
                field={'purchaseOrganization'}
                myRequestOnly={!props.selectedFilters.allSelected}
                userId={props.userId}
                getFiltersDropdownsOptions={props.getFiltersDropdownsOptions}
                loading={props.isLoadingAuthData}
                filteredOptions={props.filteredOptions}
                currentTab={props.currentTab}
            />
            <FilterAutocomplete
                id='purchasingGroupFilter'
                selectedObject={props.selectedFilters.purchasingGroup}
                label="Purchasing Group"
                handleChange={(selectedOptions) => handleChangeFilterValue(selectedOptions, 'purchasingGroup')}
                onClearOptions={() => handleClearOptions('purchasingGroup')}
                popperComponent={(val) => autocompletePopperComponent(val, 'purchasingGroup')}
                limitTags={COUNT_OF_LIMIT_TAGS}
                context={props.context}
                field={'purchasingGroup'}
                myRequestOnly={!props.selectedFilters.allSelected}
                userId={props.userId}
                getFiltersDropdownsOptions={props.getFiltersDropdownsOptions}
                loading={props.isLoadingAuthData}
                filteredOptions={props.filteredOptions}
                currentTab={props.currentTab}
            />
            <FilterAutocomplete
                id='defaultPlantFilter'
                selectedObject={props.selectedFilters.defaultPlant}
                label="Plant"
                handleChange={(selectedOptions) => handleChangeFilterValue(selectedOptions, 'defaultPlant')}
                onClearOptions={() => handleClearOptions('defaultPlant')}
                popperComponent={(val) => autocompletePopperComponent(val, 'defaultPlant')}
                limitTags={COUNT_OF_LIMIT_TAGS}
                context={props.context}
                field={'defaultPlant'}
                myRequestOnly={!props.selectedFilters.allSelected}
                userId={props.userId}
                getFiltersDropdownsOptions={props.getFiltersDropdownsOptions}
                loading={props.isLoadingAuthData}
                filteredOptions={props.filteredOptions}
                currentTab={props.currentTab}
            />
            <FilterAutocomplete
                id='defaultStorageLocationFilter'
                selectedObject={props.selectedFilters.defaultStorageLocation}
                label="Storage Location"
                handleChange={(selectedOptions) => handleChangeFilterValue(selectedOptions, 'defaultStorageLocation')}
                onClearOptions={() => handleClearOptions('defaultStorageLocation')}
                popperComponent={(val) => autocompletePopperComponent(val, 'defaultStorageLocation')}
                limitTags={COUNT_OF_LIMIT_TAGS}
                context={props.context}
                field={'defaultStorageLocation'}
                myRequestOnly={!props.selectedFilters.allSelected}
                userId={props.userId}
                getFiltersDropdownsOptions={props.getFiltersDropdownsOptions}
                currentTab={props.currentTab}
            />
        </>
        <div className={'filters-drawer-range-title'}>Requested delivery date</div>
        <div className={'filters-drawer-range'}>
            <TextField
                sx={{width: '100%'}}
                type={'date'}
                label={'Start date'}
                variant={'outlined'}
                size={'small'}
                value={props.selectedFilters?.startRequestedDeliveryDate?.selected || ''}
                onChange={(event) => handleDateInputChange('startRequestedDeliveryDate', event.target.value)}
                InputLabelProps={{
                    shrink: true,
                }}/>
            <TextField
                sx={{width: '100%'}}
                type={'date'}
                label={'End date'}
                variant={'outlined'}
                size={'small'}
                value={props.selectedFilters?.endRequestedDeliveryDate?.selected || ''}
                onChange={(event) => handleDateInputChange('endRequestedDeliveryDate', event.target.value)}
                InputLabelProps={{
                    shrink: true,
                }}/>
        </div>
        <div className={'filters-drawer-presets'}>
            {[
                {key: 'lastYear', label: 'Last Year'},
                {key: 'thisYear', label: 'This Year'},
                {key: 'last6Months', label: 'Last 6 months'},
                {key: 'lastMonth', label: 'Last month'},
            ].map((preset) => (
                <Chip
                    key={preset.key}
                    label={preset.label}
                    variant={props.selectedFilters?.deliveryDatePreset === preset.key ? 'filled' : 'outlined'}
                    color={props.selectedFilters?.deliveryDatePreset === preset.key ? 'primary' : 'default'}
                    onClick={() => handleDeliveryDatePreset(preset.key)}
                    size="small"
                />
            ))}
        </div>
        <>
            <FilterAutocomplete
                id='paymentTermsFilter'
                selectedObject={props.selectedFilters.paymentTerms}
                label="Payment Terms"
                handleChange={(selectedOptions) => handleChangeFilterValue(selectedOptions, 'paymentTerms')}
                onClearOptions={() => handleClearOptions('paymentTerms')}
                popperComponent={(val) => autocompletePopperComponent(val, 'paymentTerms')}
                limitTags={COUNT_OF_LIMIT_TAGS}
                context={props.context}
                field={'paymentTerms'}
                myRequestOnly={!props.selectedFilters.allSelected}
                userId={props.userId}
                getFiltersDropdownsOptions={props.getFiltersDropdownsOptions}
                currentTab={props.currentTab}
            />
            <FilterAutocomplete
                id='supplierStatusFilter'
                selectedObject={props.selectedFilters.supplierStatus}
                label="Supplier Status"
                handleChange={(selectedOptions) => handleChangeFilterValue(selectedOptions, 'supplierStatus')}
                onClearOptions={() => handleClearOptions('supplierStatus')}
                popperComponent={(val) => autocompletePopperComponent(val, 'supplierStatus')}
                limitTags={COUNT_OF_LIMIT_TAGS}
                context={props.context}
                field={'supplierStatus'}
                myRequestOnly={!props.selectedFilters.allSelected}
                userId={props.userId}
                getFiltersDropdownsOptions={props.getFiltersDropdownsOptions}
                currentTab={props.currentTab}
            />
        </>
    </Box>;
}


FilterView.propTypes = {
    selectedFilters: PropTypes.object,
    setSelectedFilters: PropTypes.func,
    forwardEvent: PropTypes.func,
    setFilters: PropTypes.func,
    handleClose: PropTypes.func,
    currentTab: PropTypes.string,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    userId: PropTypes.string,
    getAuthData: PropTypes.object,
    getFiltersDropdownsOptions: PropTypes.object,
    isLoadingAuthData: PropTypes.bool,
    filteredOptions: PropTypes.object,
};
